import type { MouseEvent } from 'react';
import { Icon } from '../../ui/index.js';
import { cn } from '../../lib/cn.js';

interface Props {
  name: string;
  // Click handler for the chip body (e.g. navigate to TagDetailPage).
  onClick?: () => void;
  // When set, renders a "remove" affordance on the right edge.
  onRemove?: () => void;
  // When true, mute the chip — used while waiting for an attach/detach to land.
  pending?: boolean;
  // Visual emphasis: 'on' = filled accent, 'off' = subtle border. Default 'off'.
  active?: boolean;
  className?: string;
  ariaLabel?: string;
}

// A single tag pill. Two visual modes — selectable (used in TagPicker) and
// link-like (used in chip lists on the photo viewer / album tag card).
export function TagChip({
  name,
  onClick,
  onRemove,
  pending = false,
  active = false,
  className,
  ariaLabel,
}: Props) {
  const interactive = Boolean(onClick) && !pending;
  const Tag = interactive ? 'button' : 'span';
  const label = ariaLabel ?? name;

  function handleRemove(e: MouseEvent) {
    e.stopPropagation();
    e.preventDefault();
    if (!onRemove || pending) return;
    onRemove();
  }

  return (
    <Tag
      type={interactive ? 'button' : undefined}
      onClick={interactive ? onClick : undefined}
      aria-label={label}
      aria-pressed={interactive ? active : undefined}
      disabled={interactive && pending ? true : undefined}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
        active
          ? 'border-cyan/60 bg-cyan/15 text-cyan'
          : 'border-outline bg-surface-container text-on-surface',
        interactive && !active && 'hover:border-cyan/40 hover:text-cyan',
        interactive && 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan/60',
        pending && 'opacity-60',
        className,
      )}
    >
      <span className="max-w-[14rem] truncate">{name}</span>
      {onRemove ? (
        <button
          type="button"
          onClick={handleRemove}
          className="-mr-1 inline-flex h-4 w-4 items-center justify-center rounded-full text-on-surface-variant hover:bg-white/10 hover:text-error focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-error"
          aria-label={`Rimuovi tag ${name}`}
          tabIndex={pending ? -1 : 0}
        >
          <Icon name="close" className="text-[14px]" />
        </button>
      ) : null}
    </Tag>
  );
}
